import React from 'react';

import {
    Text,
    Conditional,
    InputWithAction,
    Cross,
    Popup,
    Space,
} from '@cashfree-intl/coherent';

import MultiSelectDropdown from './MultiSelectDropdown';
import DropdownProvider from './DropdownProvider';

import {
    StyledInputWithAction,
    StyledGridV2,
    StyledButton,
    StyledChip,
    StyledChipsWrapper,
    StyledDropdown,
} from './styled';

import { UpfrontTDRFormProps, Option } from '../types';

import { PAYMENT_NETWORK_OPTIONS } from '../constants';
import { filterOutAllOption, getPGDropdownData } from '../helpers';
import { CURRENCY_SYMBOL } from 'utils/constants';


const TDRForm = ({
    values,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    formData,
    setPaymentNetwork,
    paymentMethods,
    setFormData,
    showPaymentNetwork,
    resetForm,
    gateways,
    enableSlabsForAll,
}: UpfrontTDRFormProps) => {

    const handleAmountChange = (e, eData) => {
        const pattern = /^[0-9]*[.]?[0-9]*$/;
        if (!pattern.test(e.target.value) && e.target.value) {
            return;
        }
        handleFieldChange(e, eData);
    };

    const onCrossClick = (name, selected) => () => {
        // console.log(name, selected);
        const data = values[name];
        if (data.length > 0) {
            const result = data.filter(obj => obj.value !== (selected.value || 'ALL'));
            setFieldValue(name, result);
            if (name === 'paymentNetwork') {
                setPaymentNetwork(result);
                setFieldValue('paymentMethod', []);
            }
        }
    }

    const paymentNetwork = filterOutAllOption(values.paymentNetwork);
    const paymentMethod = filterOutAllOption(values.paymentMethod);

    return (
        // @ts-ignore
        <form autoComplete="off" onSubmit={handleSubmit}>
            <StyledGridV2 className="mb-3">
                <Text>Add Upfront TDR on</Text>
                <Space alignItems="flex-start" gap={4}>
                    <DropdownProvider>
                        <Conditional if={showPaymentNetwork}>
                            <div>
                                {/* Payment Group */}
                                <MultiSelectDropdown
                                    options={PAYMENT_NETWORK_OPTIONS}
                                    name="paymentNetwork"
                                    placeholder="Select Payment Network"
                                    disabled={formData.length > 0}
                                    value={values.paymentNetwork}
                                    setFieldValue={(name, data) => {
                                        setFieldValue(name, data);
                                        setPaymentNetwork(data);
                                        if (values.paymentMethod && values.paymentMethod.length > 0)
                                            setFieldValue('paymentMethod', []);
                                    }}
                                    setFieldTouched={setFieldTouched}
                                    setFieldError={setFieldError}
                                />
                                {/* Show the error message */}
                                <Conditional
                                    if={touched.paymentNetwork && errors.paymentNetwork}
                                >
                                    <Text className="mt-1" variant="b12" color="danger">
                                        {errors.paymentNetwork}
                                    </Text>
                                </Conditional>
                                {/* Show the Chips here */}
                                <Conditional if={values.paymentNetwork?.length > 0}>
                                    <StyledChipsWrapper gap={1} alignItems="center" wrap className="mt-1">
                                        {paymentNetwork?.slice(0, 3).map((obj: Option) => {
                                            return <StyledChip key={obj.text} alignItems="center" gap={1}>
                                                <Text variant="b12">{obj.text}</Text>
                                                <Cross size="sm" onClick={onCrossClick('paymentNetwork', obj)} />
                                            </StyledChip>;
                                        })}
                                        {paymentNetwork?.slice(3)?.length > 0 && <Popup
                                            content={() => paymentNetwork?.slice(3).map(obj => (
                                                <Text key={obj.text} variant="b12" color="bodyLight">
                                                    {obj.text}
                                                </Text>
                                            ))}
                                            hoverable
                                            position="right"
                                            trigger={<Text variant="b14" color="primary" className="pointer">+{paymentNetwork?.slice(3,)?.length} More</Text>}
                                        />}
                                    </StyledChipsWrapper>
                                </Conditional>
                            </div>
                        </Conditional>
                        <div>
                            {/* Payment Method */}
                            <MultiSelectDropdown
                                options={paymentMethods}
                                name="paymentMethod"
                                placeholder="Select Payment Method"
                                disabled={formData.length > 0}
                                value={values.paymentMethod}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                setFieldError={setFieldError}
                            />
                            {/* Show the error message */}
                            <Conditional
                                if={touched.paymentMethod && errors.paymentMethod}
                            >
                                <Text className="mt-1" variant="b12" color="danger">
                                    {errors.paymentMethod}
                                </Text>
                            </Conditional>
                            {/* Show the Chips here */}
                            <Conditional if={values.paymentMethod?.length > 0}>
                                <StyledChipsWrapper gap={1} alignItems="center" wrap className="mt-1">
                                    {paymentMethod?.slice(0, 3).map((obj: Option) => {
                                        return <StyledChip key={obj.text} alignItems="center" gap={1}>
                                            <Text variant="b12">{obj.text}</Text>
                                            <Cross size="sm" onClick={onCrossClick('paymentMethod', obj)} />
                                        </StyledChip>;
                                    })}
                                    {paymentMethod?.slice(3,)?.length > 0 && <Popup
                                        content={() => paymentMethod?.slice(3).map(obj => (
                                            <Text key={obj.text} variant="b12" color="bodyLight">
                                                {obj.text}
                                            </Text>
                                        ))}
                                        hoverable
                                        position="right center"
                                        trigger={<Text variant="b14" color="primary" className="pointer">+{paymentMethod?.slice(3,)?.length} More</Text>}
                                    />}
                                </StyledChipsWrapper>
                            </Conditional>
                        </div>
                    </DropdownProvider>
                </Space>
                {/* <div /> */}
            </StyledGridV2>
            <StyledGridV2 className="mb-3 tdr-amount-fields">
                <Text>at</Text>
                <Space alignItems="flex-start" wrap="wrap" gap={4}>
                    <StyledInputWithAction>
                        <InputWithAction
                            inputLabel="Floor"
                            id="floor"
                            name="floor"
                            label={CURRENCY_SYMBOL.INR}
                            errors={errors}
                            touched={touched}
                            value={values.floor}
                            type="number"
                            placeholder="Enter Floor Value"
                            onBlur={handleFieldBlur}
                            onChange={handleAmountChange}
                        />
                    </StyledInputWithAction>
                    <StyledInputWithAction>
                        <InputWithAction
                            inputLabel="TDR Percentage"
                            id="tdr"
                            name="tdr"
                            label='%'
                            errors={touched.tdr ? errors : {}}
                            touched={touched}
                            value={values.tdr}
                            type="number"
                            placeholder="Enter TDR Amount"
                            onBlur={handleFieldBlur}
                            onChange={handleAmountChange}
                        />
                    </StyledInputWithAction>
                    <StyledInputWithAction>
                        <InputWithAction
                            inputLabel="Fixed Fee"
                            id="fixedFee"
                            name="fixedFee"
                            label={CURRENCY_SYMBOL.INR}
                            errors={touched.fixedFee ? errors : {}}
                            touched={touched}
                            value={values.fixedFee}
                            type="number"
                            placeholder="Enter Fixed Fee"
                            onBlur={handleFieldBlur}
                            onChange={handleAmountChange}
                        />
                    </StyledInputWithAction>
                    <StyledInputWithAction>
                        <InputWithAction
                            inputLabel="Ceil"
                            id="ceil"
                            name="ceil"
                            label={CURRENCY_SYMBOL.INR}
                            errors={errors}
                            touched={touched}
                            value={values.ceil}
                            type="number"
                            placeholder="Enter Ceil Value"
                            onBlur={handleFieldBlur}
                            onChange={handleAmountChange}
                        />
                    </StyledInputWithAction>
                </Space>
            </StyledGridV2>
            <Conditional if={!enableSlabsForAll}>
                <StyledGridV2>
                    <Text>Applicable on</Text>
                    <div className="mb-3">
                        <StyledDropdown
                            name="pg"
                            value={values.pg}
                            errors={errors}
                            touched={touched}
                            disabled={formData.length > 0}
                            onChange={handleFieldChange}
                            onBlur={handleFieldBlur}
                            placeholder="Select a gateway"
                            options={getPGDropdownData(gateways)}
                            selection
                            icon="chevron down"
                        />
                        <Conditional if={touched.pg && errors.pg}>
                            <Text className="mt-1" variant="b12" color="danger">
                                {errors.pg}
                            </Text>
                        </Conditional>
                    </div>
                    {/* <div /> */}
                </StyledGridV2>
            </Conditional>
            <StyledGridV2>
                <StyledButton
                    primary
                    fluid={false}
                    size="small"
                    data-event-name="Primary_Button_TDRForm_Save"
                    data-ignore-inner-text="true"
                    type="submit"
                >
                    Save
                </StyledButton>
                <StyledButton
                    fluid={false}
                    size="small"
                    secondary
                    data-event-name="Secondary_Button_TDRForm_Reset"
                    data-ignore-inner-text="true"
                    type="button"
                    onClick={() => {
                        resetForm({
                            values: {
                                tdr: '',
                                paymentNetwork: [],
                                paymentMethod: [],
                            }
                        });
                        setFormData([]);
                        // clear the form data and the selected rows as well
                    }}
                >
                    Reset
                </StyledButton>
            </StyledGridV2>
        </form>
    );
}

export default TDRForm;